import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useChatStore } from '../../stores/chatStore';
import { getAvatarUrl } from '../../lib/avatarUtils';

interface NewMessageModalProps {
    onClose: () => void;
}

/**
 * NewMessageModal - Search and start new conversation
 * Simple pubkey input for now
 */
export function NewMessageModal({ onClose }: NewMessageModalProps) {
    const [pubkeyInput, setPubkeyInput] = useState('');
    const { profile, loading } = useUserProfile(pubkeyInput.length > 10 ? pubkeyInput : null);
    const { openChat } = useChatStore();

    const handleStartChat = () => {
        if (pubkeyInput.trim()) {
            openChat(pubkeyInput.trim());
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-md bg-white dark:bg-surface-900 rounded-2xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between">
                    <h3 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">
                        Yeni Mesaj
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Search input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Kullanıcının Public Key'ini girin
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={pubkeyInput}
                                onChange={(e) => setPubkeyInput(e.target.value)}
                                placeholder="Hex pubkey..."
                                className="w-full pl-10 pr-4 py-3 bg-surface-50 dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-xl font-mono text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-mavi-500 focus:border-transparent"
                            />
                        </div>
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            💡 İpucu: Kullanıcının profilindeki public key'i kopyalayın
                        </p>
                    </div>

                    {/* Profile preview */}
                    {loading && pubkeyInput.length > 10 && (
                        <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-xl">
                            <div className="animate-pulse flex items-center gap-3">
                                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mb-2"></div>
                                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {profile && !loading && (
                        <div className="p-4 bg-mavi-50 dark:bg-mavi-900/20 rounded-xl border border-mavi-200 dark:border-mavi-800">
                            <div className="flex items-center gap-3">
                                {profile.avatar ? (
                                    <img
                                        src={profile.avatar}
                                        alt={profile.name}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <img
                                        src={getAvatarUrl(profile.name)}
                                        alt={profile.name}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                )}
                                <div className="flex-1">
                                    <p className="font-poppins font-semibold text-gray-900 dark:text-white">
                                        {profile.name}
                                    </p>
                                    {profile.about && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                                            {profile.about}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-surface-100 dark:bg-surface-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            onClick={handleStartChat}
                            disabled={!pubkeyInput.trim()}
                            className="flex-1 px-4 py-3 bg-gradient-to-br from-mavi-500 to-mavi-600 text-white rounded-xl font-medium shadow-lg shadow-mavi-500/30 hover:shadow-mavi-500/50 transition-shadow disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                        >
                            Mesaj Başlat
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
